import { ArrowDownward, ArrowUpward } from '@mui/icons-material'
import HelpIcon from '@mui/icons-material/Help'
import { Box, Grid2, TextField, Typography } from '@mui/material'
import Button from 'components/Button'
import CountUpPrice from 'components/CountUpPrice'
import { dashboardPath, reviewPath } from 'components/Routes'
import CustomTooltip from 'components/Tooltip/Tooltip'
import DefaultLayout from 'components/layout/DefaultLayout'
import LoadingWrapper from 'components/layout/LoadingWrapper'
import { DEBOUNCE_INTERVAL } from 'constants/index'
import { useFormik } from 'formik'
import {
  useReviewOrder,
  useMutateUpdateReviewOrderQuantity
} from 'hooks/useTrading'
import useProducts, { isPlatinumProduct } from 'hooks/useProducts'
import useDebounce from 'hooks/useDebounce'
import queryString from 'query-string'
import {
  ChangeEvent,
  FocusEvent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'

// Required for TypeScript to accept <model-viewer> as a valid JSX element.
// `model-viewer` is a Google web component used to render the 3D gold bar image.
// @types/react v18 no longer exposes a global JSX namespace, so custom web
// components must be explicitly declared in IntrinsicElements.
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'model-viewer': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      > & {
        src?: string
        ar?: boolean
        'camera-controls'?: boolean
        'disable-zoom'?: boolean
        poster?: string
        'shadow-intensity'?: string
        alt?: string
      }
    }
  }
}
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { formattedPrice, roundUpDollarsAndCents } from 'utils/util'
import * as Yup from 'yup'
import useStyles from './styles'

interface FormValues {
  amount: string
  weight: number
  spotPrice?: number
}

const initialValues: FormValues = {
  amount: '0',
  weight: 0
}

const validationSchema = Yup.object().shape({
  amount: Yup.string(),
  weight: Yup.number()
    .required('Quantity is required')
    .min(0.001, 'Minimum is 0.001g')
})

export default function Buy(): JSX.Element {
  const { reviewOrderId } = useParams<{ reviewOrderId: string }>()
  const { search } = useLocation()
  const { productId: productIdStr } = queryString.parse(search)
  const { buyableProducts: products, loading: productsLoading } = useProducts()
  const productId = Number(productIdStr)
  const product = products.find(p => p.id === productId)

  const { classes } = useStyles()
  const navigate = useNavigate()

  // A deep link to a product this customer is not offered (e.g. platinum with
  // the feature flag off) has nothing to buy — send them to the dashboard.
  useEffect(() => {
    if (!productsLoading && productId && products.length > 0 && !product) {
      navigate(dashboardPath(), { replace: true })
    }
  }, [navigate, product, productId, products.length, productsLoading])

  const [helperTextAmountInput, setHelperTextAmountInput] = useState<string>('')
  const [initialised, setInitialised] = useState<boolean>(false)

  const {
    values,
    errors,
    touched,
    setTouched,
    setValues,
    setErrors,
    handleSubmit,
    handleChange,
    handleBlur
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit: () => {
      navigate(
        reviewPath({
          queryParams: { type: 'buy' },
          routeParams: { reviewOrderId }
        })
      )
    },
    enableReinitialize: true
  })

  const { data: reviewOrderData, isLoading: reviewOrderLoading } =
    useReviewOrder(reviewOrderId ? Number(reviewOrderId) : null)

  // Initialise form from fetched review order (once)
  useEffect(() => {
    if (!reviewOrderData || initialised) return
    const { totalPrice, quantityMg, spotPrice } = reviewOrderData
    setValues({
      amount: totalPrice
        ? roundUpDollarsAndCents(Number(totalPrice)).toString()
        : '0',
      weight: quantityMg ? Number(quantityMg) / 1000 : 0,
      spotPrice: spotPrice ? Number(spotPrice) : undefined
    })
    setInitialised(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewOrderData, initialised])

  const spotPrice = reviewOrderData?.spotPrice
  const initialTotalFees = reviewOrderData?.totalFees
  const initialTotalPrice = reviewOrderData?.totalPrice

  const updateReviewOrderMutation = useMutateUpdateReviewOrderQuantity()
  const [updatedTotalFees, setUpdatedTotalFees] = useState<number | undefined>()
  const [updatedTotalPrice, setUpdatedTotalPrice] = useState<
    number | undefined
  >()
  const loading = updateReviewOrderMutation.isLoading

  const totalFees = useMemo(() => {
    return updatedTotalFees ? updatedTotalFees : initialTotalFees
  }, [updatedTotalFees, initialTotalFees])

  const totalPrices = useMemo(() => {
    return updatedTotalPrice ? updatedTotalPrice : initialTotalPrice
  }, [updatedTotalPrice, initialTotalPrice])

  const debouncedWeight = useDebounce<number>(values.weight, DEBOUNCE_INTERVAL)
  const debouncedAmount = useDebounce<string>(values.amount, DEBOUNCE_INTERVAL)

  useEffect(() => {
    if (!errors.weight && touched.weight) {
      handleChangeWeight(values.weight)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedWeight])

  useEffect(() => {
    if (!errors.amount && touched.amount) {
      const weight = calculateDecigramsRoundedDown(Number(values.amount))
      handleChangeWeight(weight, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedAmount])

  const handleChangeWeight = (weight: number, rounded?: boolean) => {
    updateReviewOrderMutation.mutate(
      {
        id: Number(reviewOrderId),
        quantityMg: Math.round(weight * 1000)
      },
      {
        onError: (error: unknown) => {
          const err = error as { message?: string }
          setErrors({
            weight: err?.message ?? 'Error updating',
            amount: undefined
          })
        },
        onSuccess: data => {
          const { totalPrice, quantityMg, spotPrice } = data
          setUpdatedTotalFees(
            data.totalFees ? Number(data.totalFees) : undefined
          )
          setUpdatedTotalPrice(totalPrice ? Number(totalPrice) : undefined)
          setValues({
            amount: totalPrice
              ? roundUpDollarsAndCents(Number(totalPrice)).toString()
              : '0',
            weight: quantityMg ? Number(quantityMg) / 1000 : 0,
            spotPrice: spotPrice ? Number(spotPrice) : undefined
          })
          setErrors({ weight: undefined, amount: undefined })
          setTouched({ amount: false, weight: false })
          if (
            Number(values.amount) !== Number(Number(totalPrice).toFixed(2)) &&
            rounded
          ) {
            setHelperTextAmountInput(
              `We've rounded to $${roundUpDollarsAndCents(
                Number(totalPrice)
              ).toString()}`
            )
          }
        }
      }
    )
  }

  // Quantity (in product unit_type) purchasable for a given dollar
  // amount. Names preserved for diff minimisation.
  const calculateDecigrams = useCallback(
    (amount: number) =>
      amount > 0 && Number(spotPrice) > 0
        ? Math.max(Math.round(amount / Number(spotPrice)) / 1000, 0)
        : 0,
    [spotPrice]
  )

  const calculateDecigramsRoundedDown = useCallback(
    (amount: number) =>
      amount > 0 && Number(spotPrice) > 0
        ? Math.max(Math.floor(amount / Number(spotPrice)) / 1000, 0)
        : 0,
    [spotPrice]
  )

  const handleIncreaseAmount = useCallback(() => {
    setHelperTextAmountInput('')
    if (!loading) {
      const amount = Number(values.amount) + Number(spotPrice)
      const weight = calculateDecigrams(amount)
      setTouched({ amount: false, weight: true })
      setValues({ amount: amount.toFixed(2).toString(), weight })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, spotPrice, values.amount])

  const handleDecreaseAmount = useCallback(() => {
    setHelperTextAmountInput('')
    if (!loading) {
      const amount = Number(values.amount) - Number(spotPrice)
      const weight = calculateDecigrams(amount)
      setTouched({ amount: false, weight: true })
      setValues({
        amount: amount > 0 ? amount.toFixed(2).toString() : '0',
        weight
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, spotPrice, values.amount])

  const handleChangeWeightInput = (e: ChangeEvent<HTMLInputElement>) => {
    setTouched({ amount: false, weight: true })
    handleChange(e)
    setHelperTextAmountInput('')
  }

  const handleBlurWeightInput = (e: FocusEvent<HTMLInputElement>) => {
    setTouched({ amount: false, weight: true })
    handleBlur(e)
    setHelperTextAmountInput('')
  }

  const handleChangeAmountInput = (e: ChangeEvent<HTMLInputElement>) => {
    setTouched({ amount: true, weight: false })
    handleChange(e)
    setHelperTextAmountInput('')
  }

  const renderReviewAndPay = () => {
    return (
      <Grid2 className={classes['review-&-pay']}>
        <Box component='div'>
          <CountUpPrice
            value={totalPrices ?? 0}
            prefix='$'
            duration={0.5}
            variant='body1'
          />
          <Box>
            <Typography>Includes fee of </Typography>
            <CountUpPrice value={totalFees ?? 0} prefix='$' duration={0.5} />
          </Box>
        </Box>
        <Button
          style={{ width: '100px' }}
          color='white'
          onClick={() => handleSubmit()}
        >
          Review & Buy
        </Button>
      </Grid2>
    )
  }

  return (
    <DefaultLayout
      wrapperContent={{ mt: 2 }}
      stickyFooter={renderReviewAndPay()}
      showBackIcon
      heading='Buy'
    >
      <Grid2 container>
        <Grid2 container>
          <Grid2 container gap={1}>
            <Typography variant='h3' fontWeight='800'>
              ${formattedPrice(Number(spotPrice ?? 0) * 1000)}
            </Typography>
            <CustomTooltip
              Icon={<HelpIcon color='primary' />}
              title='
                This is the buy price.
                It is the price the market will sell at currently.
                It is slightly different to the current mid-price or sell price.
              '
              placement='bottom'
            />
          </Grid2>
          <Grid2 container gap={1}>
            <Typography className={classes.perGram}>Per Gram</Typography>
            <Typography className={classes.perGram}>(NZD)</Typography>
          </Grid2>
        </Grid2>

        <Grid2 my={4} container size={{ xs: 12 }}>
          {product && isPlatinumProduct(product) ? (
            // The 3D model is a gold nugget; platinum shows its own bar instead.
            <img
              src={product.image}
              alt={`${product.name} bullion`}
              width='100%'
            />
          ) : (
            <model-viewer
              src='GoldNugget_002.glb'
              ar={false}
              camera-controls
              disable-zoom
              poster='poster.webp'
              shadow-intensity='1'
              alt='A 3D gold bar'
            ></model-viewer>
          )}
        </Grid2>

        <LoadingWrapper loading={reviewOrderLoading}>
          <form noValidate onSubmit={handleSubmit} className={classes.form}>
            <Grid2 container spacing={2}>
              <Grid2 container gap={1}>
                <Grid2>
                  <Box
                    className={classes.button}
                    onClick={handleIncreaseAmount}
                  >
                    <ArrowUpward color='primary' />
                  </Box>
                </Grid2>
                <Grid2 flex={1}>
                  <TextField
                    name='amount'
                    label='Purchase amount'
                    type='number'
                    fullWidth
                    value={values.amount}
                    disabled={loading}
                    onChange={handleChangeAmountInput}
                    error={!!errors.amount}
                    helperText={helperTextAmountInput}
                    FormHelperTextProps={{
                      className: classes.helperTextAmount
                    }}
                    InputProps={{
                      startAdornment: (
                        <Typography mt={2} ml={1}>
                          $
                        </Typography>
                      ),
                      className: classes.amountInput
                    }}
                  />
                  <Typography
                    className={
                      !!errors.amount
                        ? classes.incrementsError
                        : classes.increments
                    }
                  >
                    {`(Buy increments of $${formattedPrice(spotPrice ?? 0)})`}
                  </Typography>
                </Grid2>
                <Grid2>
                  <Box
                    className={classes.button}
                    onClick={handleDecreaseAmount}
                  >
                    <ArrowDownward color='primary' />
                  </Box>
                </Grid2>
              </Grid2>

              <Grid2 size={{ xs: 12 }}>
                <TextField
                  name='weight'
                  type='number'
                  label={`Weight in ${product?.name ?? 'product'} (grams)`}
                  fullWidth
                  disabled={loading}
                  onChange={handleChangeWeightInput}
                  onBlur={handleBlurWeightInput}
                  value={values.weight}
                  helperText={Boolean(errors.weight) && errors.weight}
                  error={Boolean(errors.weight)}
                />
              </Grid2>
            </Grid2>
          </form>
        </LoadingWrapper>
      </Grid2>
    </DefaultLayout>
  )
}
